import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * §11: "Empty invites an action."
 *
 * An empty cart that says "Your cart is empty" and stops is a dead end. This
 * component makes the action slot part of the contract, so an empty screen
 * cannot ship without one.
 */

export interface EmptyStateProps {
  illustration?: ReactNode;
  title: string;
  body?: string;
  /** Required by design: an empty state without a way out is a dead end. */
  action: ReactNode;
  secondaryAction?: ReactNode;
  className?: string;
}

export function EmptyState({
  illustration,
  title,
  body,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  return (
    <div className={cn('px-[22px] py-[38px] text-center', className)}>
      {illustration ? <div className="mx-auto mb-4 h-[78px] w-[78px]">{illustration}</div> : null}
      <h2 className="m-0 font-display text-[17px] font-bold tracking-[-0.015em]">{title}</h2>
      {body ? (
        <p className="mx-auto mb-5 mt-[7px] max-w-[30ch] text-xs leading-relaxed text-muted">
          {body}
        </p>
      ) : (
        <div className="h-5" />
      )}
      <div className="space-y-[9px]">
        {action}
        {secondaryAction}
      </div>
    </div>
  );
}

/** The empty-cart drawing from the mockups. */
export function EmptyCartArt() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden className="h-full w-full">
      <path
        d="M18 30h10l10 40h38l8-30H33"
        fill="none"
        stroke="#C3D0E3"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="43" cy="80" r="5" fill="#DCE6F5" />
      <circle cx="70" cy="80" r="5" fill="#DCE6F5" />
      <circle cx="62" cy="34" r="13" fill="none" stroke="#0F52D9" strokeWidth="3.5" />
      <path d="M72 44 l9 9" stroke="#0F52D9" strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  );
}

/** The no-results drawing: a magnifier over an empty shelf. */
export function NoResultsArt() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden className="h-full w-full">
      <rect x="14" y="26" width="72" height="14" rx="3" fill="none" stroke="#C3D0E3" strokeWidth="3.5" />
      <rect x="14" y="52" width="72" height="14" rx="3" fill="none" stroke="#C3D0E3" strokeWidth="3.5" />
      <circle cx="62" cy="62" r="16" fill="#F6F8FC" />
      <circle cx="60" cy="60" r="13" fill="none" stroke="#0F52D9" strokeWidth="3.5" />
      <path d="M70 70 l10 10" stroke="#0F52D9" strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  );
}
